import React from 'react';
import { Navigate } from 'react-router-dom';
import { Button, Form, FormGroup, FormControl } from 'react-bootstrap';
import { redirectUnlessInventory } from '../../utils/moduleAccess';
import SharedDataTable from '../../_components/Shared/SharedDataTable';
import {
  ensureSharedTableStyles,
  SHARED_TABLE_SCROLL_WRAP_CLASS,
} from '../../_components/Shared/dataTableStyles';
import config from '../../../config';
import utils from '../../utils';
import DatacoLoadingIndicator from '../../_components/DatacoLoadingIndicator';

class BomUsageByJobReport extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            jobNoKeyword: '',
            rows: [],
            uoms: [],
            totalPlannedFgQty: 0,
            totalActualFgQty: 0,
            fgSummaries: [],
        };

        this.columns = [
            { name: 'Material', selector: row => row.material || '', sortable: true, minWidth: '120px' },
            { name: 'Material Description', selector: row => row.materialDesc || '', sortable: true, minWidth: '180px' },
            { name: 'UoM', selector: row => utils.mapTo(this.state.uoms || [], row.uom) || row.uom || '', sortable: true, minWidth: '90px' },
            {
                name: 'Material Qty Used (Planned FG)',
                selector: row => Number(row.quantityUsedForPlannedFg || 0).toFixed(4),
                sortable: true,
                right: true,
                minWidth: '190px'
            },
            {
                name: 'Material Qty Used (Actual FG)',
                selector: row => Number(row.quantityUsedForActualFg || 0).toFixed(4),
                sortable: true,
                right: true,
                minWidth: '180px'
            },
            { name: 'Where Use', selector: row => row.whereUse || '', sortable: true, minWidth: '120px' },
            { name: 'Supplier', selector: row => row.supplier || '', sortable: true, minWidth: '120px' },
        ];
    }

    componentDidMount() {
        ensureSharedTableStyles();
        this.fetchUoms();
    }

    fetchUoms = () => {
        fetch(`${config.apiUrl}/getAllMaterialUom`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
        })
            .then(r => r.text().then(t => (t ? JSON.parse(t) : [])))
            .then((uoms) => {
                this.setState({ uoms: Array.isArray(uoms) ? uoms : [] });
            })
            .catch((error) => {
                console.error('Error loading UOM list:', error);
                this.setState({ uoms: [] });
            });
    }

    fetchReportRows = (url) => {
        return fetch(url, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
        }).then(r => r.text().then(t => (t ? JSON.parse(t) : [])));
    }

    rowMatchesSearchKeyword = (row, keyword) => {
        if (!keyword) return true;
        const normalizedKeyword = keyword.toLowerCase();
        return [row.jobNo, row.fgPartNo, row.partNo].some(value =>
            String(value || '').toLowerCase().indexOf(normalizedKeyword) !== -1
        );
    }

    mergeReportRows = (resultSets, keyword) => {
        const seenRows = {};
        const mergedRows = [];
        resultSets.forEach((rows) => {
            (Array.isArray(rows) ? rows : []).forEach((row) => {
                if (!this.rowMatchesSearchKeyword(row, keyword)) return;
                const key = JSON.stringify([
                    row.jobNo || '',
                    row.masterJobNo || '',
                    row.fgPartNo || '',
                    row.partNo || '',
                    row.material || '',
                    row.uom || '',
                    row.whereUse || '',
                    row.supplier || '',
                    row.plannedQty || 0,
                    row.actualQty || 0,
                    row.quantityUsedForPlannedFg || 0,
                    row.quantityUsedForActualFg || 0,
                ]);
                if (seenRows[key]) return;
                seenRows[key] = true;
                mergedRows.push(row);
            });
        });
        return mergedRows;
    }

    loadReport = () => {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        if (!user || !user.accountId) return;

        this.setState({ loading: true });
        const keyword = (this.state.jobNoKeyword || '').trim();
        const encodedKeyword = encodeURIComponent(keyword);
        const baseUrl = `${config.apiUrl}/getInventoryBomUsageReport?company=${user.accountId}`;
        const reportUrls = keyword
            ? [
                `${baseUrl}&jobNo=${encodedKeyword}`,
                `${baseUrl}&partNo=${encodedKeyword}`,
            ]
            : [baseUrl];

        Promise.all(reportUrls.map(this.fetchReportRows))
            .then((resultSets) => {
                const safeRows = this.mergeReportRows(resultSets, keyword);
                const jobTotals = {};
                safeRows.forEach((row) => {
                    const key = `${row.jobNo || ''}__${row.masterJobNo || ''}__${row.fgPartNo || ''}`;
                    if (!jobTotals[key]) {
                        jobTotals[key] = {
                            plannedQty: Number(row.plannedQty || 0),
                            actualQty: Number(row.actualQty || 0),
                        };
                    }
                });
                const totalPlannedFgQty = Object.values(jobTotals).reduce((sum, x) => sum + (x.plannedQty || 0), 0);
                const totalActualFgQty = Object.values(jobTotals).reduce((sum, x) => sum + (x.actualQty || 0), 0);
                const fgSummaries = Object.keys(jobTotals).map((key) => {
                    const [jobNo, masterJobNo, fgPartNo] = key.split('__');
                    const firstRow = safeRows.find(r =>
                        (r.jobNo || '') === jobNo &&
                        (r.masterJobNo || '') === masterJobNo &&
                        (r.fgPartNo || '') === fgPartNo
                    ) || {};
                    return {
                        jobNo,
                        masterJobNo,
                        fgPartNo,
                        fgPartDesc: firstRow.fgPartDesc || '',
                        plannedQty: jobTotals[key].plannedQty || 0,
                        actualQty: jobTotals[key].actualQty || 0,
                    };
                });
                this.setState({
                    rows: safeRows,
                    totalPlannedFgQty,
                    totalActualFgQty,
                    fgSummaries,
                    loading: false
                });
            })
            .catch((error) => {
                console.error('Error loading material usage report:', error);
                this.setState({ rows: [], totalPlannedFgQty: 0, totalActualFgQty: 0, fgSummaries: [], loading: false });
            });
    }

    exportExcel = () => {
        utils.tableToExcel('bom-usage-job-report-export-table', 'report', 'Material_Usage_Report.xls');
    }

    render() {
        const accessDenied = redirectUnlessInventory(localStorage.getItem('user'));
        if (accessDenied) return accessDenied;

        return (
            <div className="container" style={{ marginTop: '20px' }}>
                <h4>Material Usage Report</h4>
                <p className="text-muted">Filter by Job Number or Part Number and view finished goods with material quantities used.</p>

                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap' }}>
                    <Form inline>
                        <FormGroup>
                            <FormControl
                                type="text"
                                placeholder="Job Number or Part Number"
                                value={this.state.jobNoKeyword}
                                onChange={(e) => this.setState({ jobNoKeyword: e.target.value })}
                                style={{ minWidth: '250px' }}
                            />
                        </FormGroup>
                    </Form>
                    <Button bsStyle="primary" onClick={this.loadReport} disabled={this.state.loading}>
                        {this.state.loading ? 'Loading...' : 'Search'}
                    </Button>
                    <Button bsStyle="default" onClick={this.exportExcel} disabled={this.state.rows.length === 0}>
                        Export
                    </Button>
                </div>
                {(this.state.fgSummaries || []).length > 0 && (
                    <div style={{
                        marginBottom: '14px',
                        border: '1px solid #d9e8ff',
                        backgroundColor: '#f6faff',
                        borderRadius: '8px',
                        padding: '12px'
                    }}>
                        <div style={{ fontWeight: 700, color: '#2b5fab', marginBottom: '8px' }}>
                            FG Production Details
                        </div>
                        <div className={SHARED_TABLE_SCROLL_WRAP_CLASS} style={{ overflowX: 'auto' }}>
                            <table className="table table-bordered table-condensed table-hover" style={{ marginBottom: 0, backgroundColor: '#fff' }}>
                                <thead>
                                    <tr>
                                        <th>Job No</th>
                                        <th>Master Job No</th>
                                        <th>FG Part No</th>
                                        <th>FG Description</th>
                                        <th style={{ textAlign: 'right' }}>FG Planned Qty</th>
                                        <th style={{ textAlign: 'right' }}>FG Actual Qty</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(this.state.fgSummaries || []).map((fg, idx) => (
                                        <tr key={`${fg.jobNo}-${fg.fgPartNo}-${idx}`}>
                                            <td>{fg.jobNo || ''}</td>
                                            <td>{fg.masterJobNo || ''}</td>
                                            <td>{fg.fgPartNo || ''}</td>
                                            <td>{fg.fgPartDesc || ''}</td>
                                            <td style={{ textAlign: 'right' }}>{Number(fg.plannedQty || 0).toFixed(2)}</td>
                                            <td style={{ textAlign: 'right' }}>{Number(fg.actualQty || 0).toFixed(2)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                <SharedDataTable
                    columns={this.columns}
                    data={this.state.rows || []}
                    pagination
                    progressPending={this.state.loading}
                    progressComponent={<DatacoLoadingIndicator loading={this.state.loading} size="compact" label="Loading BOM usage" />}
                    getRowKey={(row, index) => `${row.jobNo || ''}-${row.partNo || ''}-${row.material || ''}-${index}`}
                />

                <div id="bom-usage-job-report-export-table" style={{ display: 'none' }}>
                    <table border="1" style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr>
                                <th>Job No</th>
                                <th>Master Job No</th>
                                <th>FG Part No</th>
                                <th>FG Description</th>
                                <th>FG Planned Qty</th>
                                <th>FG Actual Qty</th>
                                <th>Material</th>
                                <th>Material Description</th>
                                <th>UoM</th>
                                <th>Material Qty Used (Planned FG)</th>
                                <th>Material Qty Used (Actual FG)</th>
                                <th>Where Use</th>
                                <th>Supplier</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(this.state.rows || []).map((r, i) => (
                                <tr key={`${r.jobNo || 'job'}-${r.material || 'mat'}-${i}`}>
                                    <td>{r.jobNo || ''}</td>
                                    <td>{r.masterJobNo || ''}</td>
                                    <td>{r.fgPartNo || ''}</td>
                                    <td>{r.fgPartDesc || ''}</td>
                                    <td>{r.plannedQty || 0}</td>
                                    <td>{r.actualQty || 0}</td>
                                    <td>{r.material || ''}</td>
                                    <td>{r.materialDesc || ''}</td>
                                    <td>{utils.mapTo(this.state.uoms || [], r.uom) || r.uom || ''}</td>
                                    <td>{Number(r.quantityUsedForPlannedFg || 0).toFixed(4)}</td>
                                    <td>{Number(r.quantityUsedForActualFg || 0).toFixed(4)}</td>
                                    <td>{r.whereUse || ''}</td>
                                    <td>{r.supplier || ''}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }
}

export { BomUsageByJobReport };
